import styles from './Promotions.module.css';
import useInView from '../Animation/useInView.ts';
import { ReactNode } from 'react';
import { IconLibrary } from '../common/Icons.tsx';

interface Promotion {
    id: string;
    title: string;
    description: string;
    discount?: string;
    badge?: string;
    icon?: number;
    image?: string;
    validUntil?: string;
}

interface PromotionsProps {
    title?: string;
    subtitle?: string;
    promotions: Promotion[];
    columns?: 2 | 3 | 4;
    className?: string;
}

const colorSchemes = [
    // 1. Основной лавандовый (более насыщенный)
    {
        bg: 'linear-gradient(135deg, rgba(124, 58, 237, 0.22) 0%, rgba(167, 139, 250, 0.20) 100%)',
        accent: '#7c3aed',
        glow: 'rgba(124, 58, 237, 0.45)'
    },

    // 2. Светло-лавандовый (более насыщенный #a78bfa)
    {
        bg: 'linear-gradient(135deg, rgba(167, 139, 250, 0.25) 0%, rgba(196, 181, 253, 0.18) 100%)',
        accent: '#a78bfa',
        glow: 'rgba(167, 139, 250, 0.42)'
    },

    // 3. Тёмно-лавандовый (насыщенный голубой #7dd3fc)
    {
        bg: 'linear-gradient(135deg, rgba(125, 211, 252, 0.20) 0%, rgba(124, 58, 237, 0.18) 100%)',
        accent: '#7dd3fc',
        glow: 'rgba(125, 211, 252, 0.40)'
    },

    // 4. Лаванда + голубой микс (супер насыщенные)
    {
        bg: 'linear-gradient(135deg, rgba(167, 139, 250, 0.24) 0%, rgba(125, 211, 252, 0.22) 100%)',
        accent: '#a78bfa',
        glow: 'rgba(167, 139, 250, 0.48)'
    },

    // 5. Нежная сирень с ярким голубым
    {
        bg: 'linear-gradient(135deg, rgba(125, 211, 252, 0.18) 0%, rgba(167, 139, 250, 0.20) 100%)',
        accent: '#7dd3fc',
        glow: 'rgba(125, 211, 252, 0.45)'
    },

    // 6. Глубокий индиго с лавандой
    {
        bg: 'linear-gradient(135deg, rgba(124, 58, 237, 0.26) 0%, rgba(167, 139, 250, 0.22) 100%)',
        accent: '#7c3aed',
        glow: 'rgba(124, 58, 237, 0.50)'
    },

    // 7. Яркий голубой + лаванда
    {
        bg: 'linear-gradient(135deg, rgba(125, 211, 252, 0.24) 0%, rgba(167, 139, 250, 0.24) 100%)',
        accent: '#7dd3fc',
        glow: 'rgba(125, 211, 252, 0.48)'
    }
];

const Promotions = ({
    title = "Акции и спецпредложения",
    subtitle,
    promotions,
    columns = 3,
    className = ""
}: PromotionsProps) => {
    const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });

    const gridClass = {
        2: styles.grid2,
        3: styles.grid3,
        4: styles.grid4
    }[columns];

    return (
        <section id="Promotions" ref={ref} className={`${styles.promotions} ${className}`}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h2 className={`${styles.title} ${isInView ? styles.titleVisible : ''}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`${styles.subtitle} ${isInView ? styles.subtitleVisible : ''}`}>
                            {subtitle}
                        </p>
                    )}
                </div>

                <div className={`${styles.grid} ${gridClass}`}>
                    {promotions.map((promotion, index) => {
                        const colorScheme = colorSchemes[index % colorSchemes.length];
                        const IconComponent = IconLibrary[promotion.icon ?? (index % IconLibrary.length)];
                        const isDiscountNumeric = promotion.discount?.includes('%') || promotion.discount?.includes('₽');

                        return (
                            <div
                                key={promotion.id}
                                className={`${styles.card} ${isInView ? styles.cardVisible : ''}`}
                                style={{
                                    '--delay': `${index * 0.15}s`,
                                    '--card-bg': colorScheme.bg,
                                    '--card-accent': colorScheme.accent,
                                    '--card-glow': colorScheme.glow
                                } as React.CSSProperties}
                            >
                                <div className={styles.cardInner}>
                                    {promotion.badge && (
                                        <div className={styles.badge}>
                                            {promotion.badge}
                                        </div>
                                    )}

                                    <div className={styles.media}>
                                        {promotion.image ? (
                                            <img
                                                src={promotion.image}
                                                alt={promotion.title}
                                                className={styles.image}
                                            />
                                        ) : (
                                            <div className={styles.iconWrapper}>
                                                <IconComponent className={styles.icon} />
                                                <div className={styles.iconGlow} />
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.content}>
                                        {promotion.discount && (
                                            <div className={`${styles.discount} ${isDiscountNumeric ? styles.discountNumeric : styles.discountText}`}>
                                                {promotion.discount}
                                            </div>
                                        )}

                                        <h3 className={styles.promotionTitle}>
                                            {promotion.title}
                                        </h3>

                                        <p className={styles.description}>
                                            {promotion.description}
                                        </p>

                                        {promotion.validUntil && (
                                            <div className={styles.footer}>
                                                <span className={styles.validLabel}>Действует до:</span>
                                                <span className={styles.validDate}>{promotion.validUntil}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.cardHoverEffect} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Promotions;